import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { CheckCircle, Globe, ArrowRight } from "lucide-react";
import api from "@/lib/api";

const PaymentSuccessPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState("checking");

  useEffect(() => {
    const sessionId = searchParams.get("session_id");
    if (sessionId) {
      pollPaymentStatus(sessionId);
    } else {
      setStatus("error");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const pollPaymentStatus = async (sessionId, attempts = 0) => {
    const maxAttempts = 8;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      setStatus("timeout");
      return;
    }

    try {
      const data = await api.getPaymentStatus(sessionId);
      // The backend answers { status: "paid" | "unpaid" | "expired" | ... }.
      if (data.status === "paid") {
        setStatus("success");
        return;
      }
      if (data.status === "expired") {
        setStatus("expired");
        return;
      }
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), pollInterval);
    } catch (error) {
      setStatus("error");
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-secondary/50 to-background flex flex-col">
      <header className="p-4 md:p-6">
        <div className="max-w-7xl mx-auto flex items-center justify-center">
          <div className="flex items-center gap-2">
            <Globe className="h-6 w-6 text-primary" />
            <span className="font-bold text-lg font-['Manrope']">Nomad Roadmap</span>
          </div>
        </div>
      </header>

      <main className="flex-1 flex items-center justify-center p-4">
        <Card className="w-full max-w-md border-border/50 shadow-xl animate-fade-in">
          <CardContent className="p-8 text-center">
            {status === "checking" && (
              <>
                <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-6" />
                <h1 className="text-2xl font-bold font-['Manrope'] mb-2">Confirming your plan</h1>
                <p className="text-muted-foreground">One moment while we confirm with Stripe...</p>
              </>
            )}

            {status === "success" && (
              <>
                <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-6">
                  <CheckCircle className="h-10 w-10 text-green-600" />
                </div>
                <h1 className="text-2xl font-bold font-['Manrope'] mb-2">You're in.</h1>
                <p className="text-muted-foreground mb-6">
                  Your first 3 months are active. After that it's $6.99/month - you can cancel
                  anytime from your profile.
                </p>
                <Button
                  onClick={() => navigate("/dashboard")}
                  className="w-full bg-primary hover:bg-primary/90"
                  data-testid="go-to-dashboard-btn"
                >
                  Go to Dashboard
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </>
            )}

            {status === "timeout" && (
              <>
                <div className="w-16 h-16 rounded-full bg-yellow-100 flex items-center justify-center mx-auto mb-6">
                  <span className="text-3xl">⏳</span>
                </div>
                <h1 className="text-2xl font-bold font-['Manrope'] mb-2">Still confirming</h1>
                <p className="text-muted-foreground mb-6">
                  Your payment went through and your access will appear within a minute. You'll
                  also get a receipt from Stripe by email.
                </p>
                <Button onClick={() => navigate("/dashboard")} variant="outline" className="w-full">
                  Go to Dashboard
                </Button>
              </>
            )}

            {status === "expired" && (
              <>
                <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-6">
                  <span className="text-3xl">⚠️</span>
                </div>
                <h1 className="text-2xl font-bold font-['Manrope'] mb-2">Session expired</h1>
                <p className="text-muted-foreground mb-6">
                  That checkout session expired before it finished. Nothing was charged.
                </p>
                <Button onClick={() => navigate("/pricing")} className="w-full">
                  Return to Pricing
                </Button>
              </>
            )}

            {status === "error" && (
              <>
                <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-6">
                  <span className="text-3xl">❌</span>
                </div>
                <h1 className="text-2xl font-bold font-['Manrope'] mb-2">Something went wrong</h1>
                <p className="text-muted-foreground mb-6">
                  We couldn't verify your payment. If you were charged, email
                  info.nomadroadmap@gmail.com and we'll sort it out.
                </p>
                <Button onClick={() => navigate("/pricing")} className="w-full">
                  Return to Pricing
                </Button>
              </>
            )}
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default PaymentSuccessPage;
